import { Immutable, toImmutable } from 'nuclear-js';

import DataUtils from 'data/utils';
import TermUtils from './terms';
import PopoverUtils from './chart-popovers';
import CommonValues from 'resources/common-values';

// Linking studies to show within the GOT chart
export const collegeLinkingStudyTypes = ['ACT', 'SAT'];

// This logic needs to match the color styling logic for the tabs.  That currently
// resides in utils/styles.js.  It would be nice to centralize this logic so the tab
// colors are always the same as the RIT score colors on the GOT chart.
export function findQuintileColorForTestEvent(score, termQuintileData) {
    if (!termQuintileData || !score) return null;

    return !termQuintileData.get(20, null)
        ? CommonValues.quintileColors['6'] // gray
        : score <= termQuintileData.get(20, null)
        ? CommonValues.quintileColors['1']
        : score <= termQuintileData.get(40, null)
        ? CommonValues.quintileColors['2']
        : score <= termQuintileData.get(60, null)
        ? CommonValues.quintileColors['3']
        : score <= termQuintileData.get(80, null)
        ? CommonValues.quintileColors['4']
        : CommonValues.quintileColors['5'];
}

export const populateRitQuintileSeries = (sortedTerms, ritQuintiles) => {
    const quintile1Data = [],
        quintile2Data = [],
        quintile3Data = [],
        quintile4Data = [],
        quintile5Data = [],
        noData = [];

    let seriesPopulated = false;

    if (sortedTerms && ritQuintiles && !sortedTerms.isEmpty()) {
        // in order to make the quintile data reach the end of the graph, create
        //    bogus start and end points that are the same as the first term
        const firstTermNumber = sortedTerms.first().get('termNumber');
        // default to empty map so that the subsequent statements will not cause exceptions
        const firstTermQuintileData = ritQuintiles.get(firstTermNumber)
            ? ritQuintiles.get(firstTermNumber)
            : Immutable.Map();
        quintile1Data.push({
            y: firstTermQuintileData.get(20, null) !== 0 ? firstTermQuintileData.get(20, null) : null,
        });
        quintile2Data.push({
            y: firstTermQuintileData.get(40, null) !== 0 ? firstTermQuintileData.get(40, null) : null,
        });
        quintile3Data.push({
            y: firstTermQuintileData.get(60, null) !== 0 ? firstTermQuintileData.get(60, null) : null,
        });
        quintile4Data.push({
            y: firstTermQuintileData.get(80, null) !== 0 ? firstTermQuintileData.get(80, null) : null,
        });
        // We don't actually have a max value for the top quintile, so use MAX_RIT if the other quintiles are
        //      populated.  Otherwise, use null so that the entire graph is not red
        quintile5Data.push({ y: firstTermQuintileData.get(80) ? CommonValues.MAX_RIT : null });
        noData.push({ y: CommonValues.MAX_RIT });

        // add data for each term
        sortedTerms.forEach((t) => {
            const quintileData = ritQuintiles.get(t.get('termNumber'))
                ? ritQuintiles.get(t.get('termNumber'))
                : Immutable.Map();
            quintile1Data.push({ y: quintileData.get(20, null) !== 0 ? quintileData.get(20, null) : null });
            quintile2Data.push({ y: quintileData.get(40, null) !== 0 ? quintileData.get(40, null) : null });
            quintile3Data.push({ y: quintileData.get(60, null) !== 0 ? quintileData.get(60, null) : null });
            quintile4Data.push({ y: quintileData.get(80, null) !== 0 ? quintileData.get(80, null) : null });
            quintile5Data.push({ y: quintileData.get(80) ? CommonValues.MAX_RIT : null });
            noData.push({ y: CommonValues.MAX_RIT });

            if (quintile1Data.y || quintile2Data.y || quintile3Data.y || quintile4Data.y || quintile5Data.y) {
                seriesPopulated = true;
            }
        });

        // add bogus end point
        const lastTermNumber = sortedTerms.last().get('termNumber');
        const lastTermQuintileData = ritQuintiles.get(lastTermNumber)
            ? ritQuintiles.get(lastTermNumber)
            : Immutable.Map();
        quintile1Data.push({ y: lastTermQuintileData.get(20, null) !== 0 ? lastTermQuintileData.get(20, null) : null });
        quintile2Data.push({ y: lastTermQuintileData.get(40, null) !== 0 ? lastTermQuintileData.get(40, null) : null });
        quintile3Data.push({ y: lastTermQuintileData.get(60, null) !== 0 ? lastTermQuintileData.get(60, null) : null });
        quintile4Data.push({ y: lastTermQuintileData.get(80, null) !== 0 ? lastTermQuintileData.get(80, null) : null });
        quintile5Data.push({ y: lastTermQuintileData.get(80) ? CommonValues.MAX_RIT : null });
        noData.push({ y: CommonValues.MAX_RIT });
    }

    return {
        quintile1Data,
        quintile2Data,
        quintile3Data,
        quintile4Data,
        quintile5Data,
        noData,
        quintileSeriesPopulated: seriesPopulated,
    };
};

export const findRitQuintilesForChart = (terms, subject, ritQuintiles) => {
    if (!terms || !subject || !ritQuintiles) {
        return null;
    }

    const quintileKeys = toImmutable(DataUtils.buildRitQuintileKeyset(terms, Immutable.List.of(subject)));
    return Immutable.Map().withMutations((q) => {
        quintileKeys.forEach((k) => {
            // builds a map where termNumber is key
            q.set(k.get('termNumber'), ritQuintiles.get(k, null));
        });
    });
};

export const findDistrictAveragesForChart = (terms, subject, districtAverages) => {
    if (!terms || !subject || !districtAverages) {
        return null;
    }

    const districtAveragesKeys = toImmutable(DataUtils.buildDistrictAverageKeyset(terms, Immutable.List.of(subject)));
    return Immutable.Map().withMutations((da) => {
        districtAveragesKeys.forEach((k) => {
            // builds a map where termNumber is key
            da.set(k.get('termNumber'), districtAverages.get(k, null));
        });
    });
};

export const findTermsForChart = (terms, growthGoalYn, maxTestEvent, currentTerm, studentTermData) => {
    if (!terms || !currentTerm || (growthGoalYn && !maxTestEvent) || !studentTermData || studentTermData.isEmpty()) {
        return Immutable.List();
    }

    const baseTerm = growthGoalYn ? maxTestEvent.get('termNumber') : currentTerm.get('termNumber');
    const maxTerm = baseTerm + 100;
    const maxStudentTermData = studentTermData.maxBy((t) => t.get('termNumber')).get('termNumber');
    const endTerm = Math.min(maxTerm, maxStudentTermData);

    const minTerm = growthGoalYn ? baseTerm - 100 : baseTerm;

    // filter out summer terms + anything < min or > max
    return terms
        .filter((t) => {
            return t.get('termNumber') % 10 !== 3 && t.get('termNumber') >= minTerm && t.get('termNumber') <= endTerm;
        })
        .sort((t1, t2) => t1.get('termNumber') - t2.get('termNumber'));
};

export function findTermsForGOTChart(selectedTermNumber, isFamilyReport = false) {
    if (!selectedTermNumber) {
        return null;
    }
    const numberOfTermsInChart = isFamilyReport ? CommonValues.NUM_FAMILY_GOT_TERMS : CommonValues.NUM_GOT_TERMS;
    let termsToGoBack;

    if (isFamilyReport) {
        // if ending term is fall, set term to go back to spring (not summer).            // AY = 100
        termsToGoBack =
            selectedTermNumber % 100 === CommonValues.FALL_TERM
                ? CommonValues.AY_PLUS_TWO_TERMS
                : CommonValues.AY_PLUS_ONE_TERM;
    } else {
        termsToGoBack = CommonValues.ONE_AY * 3;
    }

    let termNumbers = [];
    let beginningTerm = selectedTermNumber - termsToGoBack;

    if (isFamilyReport && beginningTerm % 100 === 0) {
        beginningTerm = beginningTerm - 96;
    }

    let i;

    for (i = 0; i < numberOfTermsInChart; i++) {
        let termMap = new Map();
        const term = beginningTerm % 100;

        //If we're printing the family report, we want to first push the term, then calculate the next term.
        if (isFamilyReport) {
            termMap.set('termNumber', beginningTerm);
            termNumbers.push(termMap);
        }

        if (term === CommonValues.FALL_TERM) {
            beginningTerm = beginningTerm + CommonValues.FALL_TO_WINTER_TERM;
        } else if (term === CommonValues.SPRING_TERM) {
            beginningTerm = beginningTerm + CommonValues.SPRING_TO_FALL_TERM;
        } else {
            beginningTerm = beginningTerm + CommonValues.WINTER_TO_SPRING_TERM;
        }

        //If we're creating a regular GOT chart, we want to calculate the term first.
        if (!isFamilyReport) {
            termMap.set('termNumber', beginningTerm);
            termNumbers.push(termMap);
        }
    }
    return toImmutable(termNumbers);
}

export const addGradeAndWeeksToTerms = (partnerTerms, studentTermData) => {
    if (!partnerTerms || !studentTermData) {
        return null;
    }

    return partnerTerms.map((t) => {
        const studentData = studentTermData.find((st) => st.get('termNumber') === t.get('termNumber'));
        const grade = studentData ? studentData.get('grade', null) : null;
        const nweaGradeName = studentData ? studentData.get('nweaGradeName', null) : null;
        const weeks =
            studentData && studentData.get('weeks', false)
                ? studentData.get('weeks')
                : t.get('instructionalWeek', null);

        return t.withMutations((term) => {
            term.set('grade', grade);
            term.set('nweaGradeName', nweaGradeName);
            term.set('weeks', weeks);
        });
    });
};

export const addGradeAndWeeksToGOTTerms = (partnerTerms, studentTermData, chartTermList) => {
    if (!partnerTerms || !studentTermData || !chartTermList) {
        return null;
    }

    const partnerTermsWithGradeAndWeeks = addGradeAndWeeksToTerms(partnerTerms, studentTermData);

    return chartTermList.map((ct) => {
        const partnerData = partnerTermsWithGradeAndWeeks.find((pt) => pt.get('termNumber') === ct.get('termNumber'));
        const grade = partnerData ? partnerData.get('grade', null) : null;
        const nweaGradeName = partnerData ? partnerData.get('nweaGradeName', null) : null;
        const weeks = partnerData ? partnerData.get('weeks', null) : null;

        ct = ct.set('grade', grade);
        ct = ct.set('nweaGradeName', nweaGradeName);
        ct = ct.set('weeks', weeks);
        return ct;
    });
};

export function populateCategoryNames(sortedTerms, isFamilyReport = false) {
    let series = [];

    if (!isFamilyReport) {
        series.push(''); // The first element doesn't actually show on the graph, so leave it blank.
    }
    /* check if exists - TP 215501 - causes crash on clearState() when logging out */
    if (sortedTerms && sortedTerms.size > 0) {
        sortedTerms.forEach((term) => {
            const termId = term.get('termNumber');
            const termName = TermUtils.textFromTermNumber(termId, false, isFamilyReport);
            const grade = term.get('nweaGradeName', null);
            series.push(termId % 10 === 4 ? (!grade ? termName : termName + (' (Gr ' + grade + ')')) : termName);
        });
    }
    series.push(''); // add fake last element too
    return series;
}

export function addPlotLine(value, chart, plotLineOptions, plotLineId) {
    chart.xAxis[0].removePlotLine(plotLineId);
    chart.xAxis[0].addPlotLine({
        ...plotLineOptions,
        value: value,
        id: plotLineId,
    });
}

export function findMaxForSeries(series) {
    // series is an array of array of maps containing y:;
    return series
        .reduce((a, b) => a.concat(b), []) // combine arrays into 1
        .reduce((currentMax, nextPoint) => {
            return nextPoint.y ? Math.max(currentMax, nextPoint.y) : currentMax;
        }, CommonValues.MIN_RIT);
}

export function findMinForSeries(series) {
    // series is an array of array of maps containing y:;
    return series
        .reduce((a, b) => a.concat(b), []) // combine arrays into 1
        .reduce((currentMin, nextPoint) => {
            return nextPoint.y ? Math.min(currentMin, nextPoint.y) : currentMin;
        }, CommonValues.MAX_RIT);
}

export function populateGrowthGoalSeriesData(termList, growthGoals, testSeriesOptions) {
    if (!termList || !growthGoals) {
        return null;
    }
    const seriesData = [];
    let seriesPopulated = 0;

    seriesData.push({
        y: null,
    });

    termList.forEach((t) => {
        const goal = growthGoals.find((g) => g.get('endTermNumber') === t.get('termNumber'));
        seriesData.push({
            y: goal ? goal.get('targetScore') : null,
            term: t.get('termNumber'),
            predictedGrowth: goal ? goal.get('predictedGrowth') : null,
        });
        if (goal) {
            seriesPopulated++;
        }
    });

    return seriesPopulated > 0 ? seriesData : null;
}

export function populateRITScoreSeries(
    termList,
    subjectBid,
    testEvents,
    ritQuintiles,
    measurementScaleID,
    isFamilyReport = false
) {
    if (!termList || !testEvents) {
        return null;
    }

    let series = [];
    const scaleFactor = __BATCH__ ? 0.75 : 1;
    const CENTERED_TERM_INDEX = 8;
    const RADIUS_SELECTED_TERM = 7 * scaleFactor;
    const LINE_WIDTH_SELECTED_TERM = 3 * scaleFactor;
    let seriesPopulated = -1;
    const numberOfTerms = termList.size;

    if (!isFamilyReport) {
        series.push({
            y: null,
        });
    }
    for (let i = 0; i < numberOfTerms; i++) {
        let termNumber = termList.get(i).get('termNumber');
        // Course Specific update - if course specific use msid
        let testEventForTermAndSubject = DataUtils.findTestEventForTermAndMsid(
            testEvents,
            termNumber,
            measurementScaleID ? measurementScaleID : subjectBid
        );
        let score = testEventForTermAndSubject ? testEventForTermAndSubject.get('score') : null;

        if (!isFamilyReport) {
            let radius = i === CENTERED_TERM_INDEX ? RADIUS_SELECTED_TERM : 4 * scaleFactor;
            let lineWidth = i === CENTERED_TERM_INDEX ? LINE_WIDTH_SELECTED_TERM : 1 * scaleFactor;
            let quintileColor = ritQuintiles
                ? findQuintileColorForTestEvent(score, ritQuintiles.get(termNumber))
                : null;
            series.push({
                y: score,
                marker: {
                    fillColor: quintileColor,
                    radius: radius,
                    states: {
                        hover: {
                            enable: true,
                            radius: radius,
                            lineWidth: lineWidth,
                        },
                    },
                },
                dataLabels: {
                    borderColor: quintileColor,
                    color: quintileColor,
                },
            });
        } else {
            series.push({
                y: score,
            });
        }

        if (testEventForTermAndSubject) {
            seriesPopulated++;
        }
    }

    if (seriesPopulated >= 0 && !isFamilyReport) {
        series.filter((t) => t.y != null)[seriesPopulated].marker.radius = 7 * scaleFactor;
        series.filter((t) => t.y != null)[seriesPopulated].marker.lineWidth = 3 * scaleFactor;
        series.filter((t) => t.y != null)[seriesPopulated].marker.states.hover.radius = 7 * scaleFactor;
        series.filter((t) => t.y != null)[seriesPopulated].marker.states.hover.lineWidth = 3 * scaleFactor;
    }

    return seriesPopulated >= 0 ? series : null;
}

export function populateTypicalScoreSeries(sortedTerms, ritQuintileData, isFamilyReport = false) {
    if (!sortedTerms || !ritQuintileData) {
        return null;
    }

    let series = [];
    let seriesPopulated = 0;

    //We don't need an extra series value if we're printing the family report.
    if (!isFamilyReport) {
        series.push({
            y: null,
        });
    }

    sortedTerms.forEach((term) => {
        const data = ritQuintileData ? ritQuintileData.get(term.get('termNumber')) : null;
        const typicalScore = data ? data.get(50, null) : null;
        series.push({
            y: typicalScore === 0 ? null : typicalScore,
        });

        if (typicalScore) {
            seriesPopulated++;
        }
    });
    return seriesPopulated > 0 ? series : null;
}

export function populateDistrictAveragesSeries(sortedTerms, districtAverageData) {
    if (!sortedTerms || !districtAverageData) {
        return null;
    }

    let series = [];
    let seriesPopulated = 0;

    series.push({
        y: null,
    });

    if (!!districtAverageData) {
        sortedTerms.forEach((term) => {
            const da = districtAverageData.get(term.get('termNumber'));
            series.push({
                y: da && da.get('mean') ? Math.round(parseFloat(da.get('mean'))) : null,
            });

            if (da && da.get('mean')) {
                seriesPopulated++;
            }
        });
    }

    return seriesPopulated > 0 ? enableDiscreteDataPoints(series) : null;
}

// This function assumes that an empty term is at the beginning and end of the series being supplied.
export function extractDiscreteQuintileData(series) {
    if (
        !series ||
        !series.quintile1Data ||
        !series.quintile2Data ||
        !series.quintile3Data ||
        !series.quintile4Data ||
        !series.quintile5Data
    ) {
        return null;
    }

    // The method is reliant on specific quintile names because downstream components access them by name
    const seriesNames = ['quintile1Data', 'quintile2Data', 'quintile3Data', 'quintile4Data', 'quintile5Data'];
    let discreteQuintileData = {};
    let hasFoundDiscreteData = false;

    seriesNames.forEach((name) => {
        let thisSeries = series[name];
        let newSeries = [];
        for (let i = 0; i < thisSeries.length; i++) {
            // Ensure that there are points ahead and behind our index, and our index has a y value
            if (!thisSeries[i] || !thisSeries[i].y || !thisSeries[i - 1] || !thisSeries[i + 1]) {
                newSeries.push({ y: null });
            } else if (thisSeries[i - 1].y === null && thisSeries[i + 1].y === null) {
                newSeries.push({ y: thisSeries[i].y }, { y: thisSeries[i].y }); // Push twice so we can fake a 'trend' for the graph

                hasFoundDiscreteData = true;
                i++; // Double increment to skip the value we just assigned when looping through again
            } else {
                newSeries.push({ y: null });
            }
            discreteQuintileData[name] = newSeries;
        }
    });

    return hasFoundDiscreteData ? discreteQuintileData : null;
}

export function populateGoalSeries(sortedTerms, growthGoals) {
    if (!sortedTerms || !growthGoals) {
        return null;
    }

    let series = [];
    let seriesPopulated = 0;

    series.push({
        y: null,
    });

    sortedTerms.forEach((term) => {
        const goal = growthGoals.find((g) => g.get('endTermNumber') === term.get('termNumber'));
        series.push({
            y: goal ? goal.get('targetScore') : null,
            predictedGrowth: goal ? goal.get('predictedGrowth') : null,
        });

        if (goal && goal.get('targetScore')) {
            seriesPopulated++;
        }
    });

    return seriesPopulated > 0 ? series : null;
}

export function populateXAxisMarkerSeries(numTerms, chartMax, termToHighlight) {
    let index = 1;
    let series = [];

    for (index; index < numTerms - 1; index++) {
        series.push({
            y: chartMax,
            x: index,
        });

        if (termToHighlight === index) {
            series[series.length - 1].color = 'rgba(255,255,255,0.6)';
        }
    }

    return series;
}

export function createDataSeries(options, eventFunctions, data = []) {
    if (eventFunctions) {
        return {
            ...options,
            data: data,
            point: {
                events: eventFunctions,
            },
        };
    } else {
        return {
            ...options,
            data,
        };
    }
}

export function populateProjectionSeries(sortedTerms, testToProjectFrom, growthProjectionData) {
    if (!sortedTerms || !testToProjectFrom || !growthProjectionData) {
        return null;
    }

    let series = [];
    let seriesPopulated = 0;

    series.push({
        y: null,
    });

    const termToProjectFrom = testToProjectFrom ? testToProjectFrom.get('termNumber') : null;

    sortedTerms.forEach((term) => {
        const termId = term.get('termNumber');
        if (termId === termToProjectFrom) {
            // add a point here so that the projection line extends from the test event that is used for
            //     projection data
            series.push({
                y: testToProjectFrom.get('score'),
                marker: {
                    // make this not show
                    lineWidth: 0,
                    radius: 0,
                },
            });
        } else {
            const projection = growthProjectionData
                ? growthProjectionData.find((p) => p.get('endingTerm') === termId)
                : null;
            const predictedScore =
                projection && projection.get('predictedScore') ? Math.round(projection.get('predictedScore')) : null;
            series.push({
                y: predictedScore,
            });

            if (projection) {
                seriesPopulated++;
            }
        }
    });

    return seriesPopulated > 0 ? series : null;
}

export function findLinkingStudyByType(
    linkingStudyData,
    linkingStudyType,
    lastTermNumberForChart,
    partnerStudies,
    collegeLinkingStudy = null
) {
    let linkingStudy = null;

    const partnerStudiesByType = partnerStudies
        ? partnerStudies.filter((ps) => ps.get('studyType') === linkingStudyType)
        : null;
    const linkingStudiesByType = linkingStudyData
        ? linkingStudyData.filter((ls) => ls.get('studyType') === linkingStudyType)
        : null;

    const linkingStudies = linkingStudiesByType
        ? linkingStudiesByType.filter((ls) => ls.get('termNumber') <= lastTermNumberForChart)
        : null;

    if ((!linkingStudies || linkingStudies.size === 0) && linkingStudyType === 'STATE') {
        //Get DEFAULT_STATE linking study
        linkingStudy = linkingStudyData ? linkingStudyData.filter((t) => t.get('studyType') === 'DEFAULT_STATE') : null;

        if (linkingStudy && linkingStudy.size === 0) {
            linkingStudy = null;
        }

        if (linkingStudy && !linkingStudy.isEmpty()) {
            linkingStudy = linkingStudy.min((info1, info2) => {
                return info1.get('termNumber') - info2.get('termNumber');
            });
        }
    } else if (!!linkingStudies && !!partnerStudiesByType) {
        // We want to get the linking study cut scores associated with the partner study
        // that is either the latest study or is the top in alphabetical order.
        // We want to be able to stop looking once we have found one to display.
        partnerStudiesByType.some((ps) => {
            const studiesByPartner = linkingStudies
                ? linkingStudies.filter((ls) => {
                      return collegeLinkingStudy
                          ? ls.get('study') === ps.get('study') && ls.get('study') === collegeLinkingStudy
                          : ls.get('study') === ps.get('study');
                  })
                : null;

            linkingStudy = studiesByPartner
                ? studiesByPartner
                      .sort((info1, info2) => {
                          return info2.get('study') - info1.get('study');
                      })
                      .min((info1, info2) => info1.get('termNumber') - info2.get('termNumber'))
                : null;

            //If we found a linking study to display we can exit
            return linkingStudy ? true : false;
        });
    }

    return linkingStudy ? linkingStudy : null;
}

export function populateLinkingStudySeries(study, terms, chartMax, isGotChart, linkingStudyOptions, chartBottom) {
    if (!study || !terms) {
        return null;
    }
    const cutScores = study.get('cutScores');
    if (!cutScores) return null;

    let series = [];
    const alphaValue = isGotChart ? 0.1 : 0.5;
    const textAlphaValue = isGotChart ? 0.1 : 1;
    const dataLabelHeight = 8;
    let rgb = 20;
    let linkingStudyYear = study.get('termNumber');
    let studyName = study.get('study');
    let leftOrRight = study.get('studyType') === 'STATE' ? 'right' : 'left';

    cutScores
        .sort((s1, s2) => s2.get('displayOrder') - s1.get('displayOrder'))
        .forEach((s) => {
            let data = [];

            data.push({
                y: null,
            });

            terms.forEach((t) => {
                const maxVal =
                    s.get('ritMax') > CommonValues.MAX_CHART_VALUE ? CommonValues.MAX_CHART_VALUE : s.get('ritMax');
                const minVal = s.get('displayOrder') === 1 ? 0 : s.get('ritMin'); // for the first 1 set min rit to 0

                if (t.get('termNumber') === linkingStudyYear) {
                    data.push({
                        y: maxVal - minVal + 1,
                    });
                } else {
                    data.push({
                        y: null,
                    });
                }
            });
            const backgroundColor = `rgba(${rgb},${rgb},${rgb},${alphaValue})`;
            rgb = rgb + 90;
            let seriesData = {
                ...linkingStudyOptions,
                name: studyName + s.get('category'),
                data: data,
                stack: studyName,
                color: backgroundColor,
                zIndex: 20 + s.get('displayOrder'),
                pointPlacement: leftOrRight === 'left' ? -0.07 : 0.07,
                enableMouseTracking: false,
                dataLabels: {
                    enabled: true,
                    allowOverlap: true,
                    backgroundColor,
                    useHTML: isGotChart ? false : true,
                    x: leftOrRight === 'left' ? -4 : 5, //maxwidth + border
                    align: leftOrRight === 'left' ? 'right' : 'left',
                    style: { opacity: textAlphaValue, textOutline: false },
                    className: leftOrRight === 'left' ? 'linking-study-label-left' : 'linking-study-label-right',
                    formatter: function() {
                        if (
                            (chartMax <= s.get('ritMax') && chartMax - s.get('ritMin') <= dataLabelHeight) ||
                            chartBottom - s.get('ritMax') >= dataLabelHeight ||
                            s.get('ritMax') - s.get('ritMin') >= dataLabelHeight
                        ) {
                            const labelClass =
                                leftOrRight === 'left' ? 'linking-study-label-left' : 'linking-study-label-right';

                            return `<div class="${labelClass}"><div class="study-name">${studyName}</div><br><div class="category">${s.get(
                                'category'
                            )}</div></div>`;
                        }
                    },
                },
            };
            if (isGotChart) {
                seriesData.dataLabels.style.color = '@gray';
                seriesData.dataLabels.style.fontWeight = 'bold';
            }
            series.push(seriesData);
        });

    return series;
}

export function findTestToProjectFrom(testEvents, currentTerm) {
    // find the most recent test event within one academic year
    // of the current term
    if (testEvents && currentTerm) {
        const minTerm = currentTerm.get('termNumber') - 100;
        const testEvent = testEvents
            .filter((te) => te.get('termNumber') >= minTerm && te.get('termNumber') <= currentTerm.get('termNumber'))
            .maxBy((te) => te.get('termNumber'));
        return testEvent ? testEvent : null;
    } else {
        return null;
    }
}

export function populateTermDataPopoverSeries(dataArrays, currentTerm) {
    if (!dataArrays) {
        return null;
    }
    const chartMax = 400;
    const sortedTerms = dataArrays.sortedTerms;
    const testEvents = dataArrays.testEvents;
    const districtAverages = dataArrays.districtAverages;
    const typicalGrowth = dataArrays.typicalGrowth;
    const firstOpenTerm = dataArrays.firstOpenTerm;
    const goals = dataArrays.goals;
    const projectedGrowth = dataArrays.projectedGrowth;
    const studentHasGradeForCurrentAY = dataArrays.studentHasGradeForCurrentAY;
    const collegeLinkingStudies = dataArrays.collegeLinkingStudies;
    const stateLinkingStudy = dataArrays.stateLinkingStudy;
    const lastTestEventTerm = dataArrays.testToProjectFrom ? dataArrays.testToProjectFrom.get('termNumber') : null;
    const goalSettingTerms = dataArrays.goalSettingTerms;

    let index = 0;
    let series = [{ y: null, x: 0 }];

    sortedTerms.forEach((term) => {
        let sections = [];
        index++;
        const termNumber = term.get('termNumber');
        const termName = TermUtils.textFromTermNumber(termNumber, true);
        sections = sections.concat(PopoverUtils.buildTestEventSection(termNumber, testEvents));

        let termGoal = goals ? goals[index].y : null;
        let predictedGrowth = goals ? goals[index].predictedGrowth : null;

        sections = sections.concat(
            PopoverUtils.buildRitScoreSection(
                termNumber,
                termGoal,
                projectedGrowth,
                index,
                predictedGrowth,
                lastTestEventTerm
            )
        );

        let allowSetGoal =
            currentTerm &&
            currentTerm.get('termNumber') <= termNumber &&
            !termGoal &&
            goalSettingTerms.find((t) => t.get('termNumber') === termNumber)
                ? true
                : false;
        if (allowSetGoal && (!firstOpenTerm || !studentHasGradeForCurrentAY)) {
            allowSetGoal = false;
        }

        if (
            currentTerm &&
            currentTerm.get('termNumber') <= termNumber &&
            (collegeLinkingStudies || stateLinkingStudy)
        ) {
            sections = sections.concat(
                PopoverUtils.buildLinkingStudiesSection(
                    termNumber,
                    collegeLinkingStudies,
                    stateLinkingStudy,
                    index,
                    termGoal,
                    projectedGrowth,
                    predictedGrowth
                )
            );
        }

        sections = sections.concat(
            PopoverUtils.buildDistrictGradeLevelMeanAndTypicalGrowthSection(
                termNumber,
                districtAverages,
                typicalGrowth,
                index
            )
        );

        const barValue = sections.length > 0 ? chartMax : null;
        series.push({
            y: barValue,
            x: index,
            nweaContent: {
                term: termNumber,
                header: termName,
                sections: sections,
                allowSetGoal: allowSetGoal,
            },
        });
    });

    return series;
}

export function populateCurrentGoalData(
    sortedTerms,
    endingTermId,
    testToProjectFrom,
    growthGoalValue,
    TestSeriesOptions,
    growthGoals
) {
    if (!sortedTerms || !growthGoals) {
        return null;
    }
    let series = [];
    let seriesPopulated = 0;
    const termToProjectFrom = testToProjectFrom ? testToProjectFrom.get('termNumber') : null;

    // add bogus entry before first term to match other data series
    series.push({
        y: null,
    });

    sortedTerms.forEach((term) => {
        const termId = term.get('termNumber');

        // add a point here so that it is clear the current goal is being projected from
        //     a test event
        if (termId === termToProjectFrom) {
            series.push({
                y: testToProjectFrom.get('score'),
                term: termId,
                marker: {
                    ...TestSeriesOptions.marker,
                    states: {
                        hover: {
                            enabled: false,
                        },
                    },
                },
                dataLabels: {
                    enabled: false,
                },
            });
            seriesPopulated++;
        } else if (termId !== endingTermId && termId > termToProjectFrom) {
            const goal = growthGoals.find((g) => g.get('endTermNumber') === termId);
            series.push({
                y: goal ? goal.get('targetScore') : null,
                term: termId,
                predictedGrowth: goal ? goal.get('predictedGrowth') : null,
                marker: {
                    ...TestSeriesOptions.marker,
                    states: {
                        hover: {
                            enabled: false,
                        },
                    },
                },
                dataLabels: {
                    enabled: false,
                },
            });
        } else if (termId === endingTermId) {
            series.push({
                y: growthGoalValue,
                term: termId,
            });
            seriesPopulated++;
        } else {
            series.push({
                y: null,
            });
        }
    });
    return seriesPopulated > 0 ? series : null;
}

export function dataLabelFormatting(chart, seriesIndex, chartId, addEventListener = true, sizeRatio = 1) {
    // sizeRatio is used to adjust the size of nodes for things like when Mac Chrome is printing.
    let gotChart = document.getElementById(chartId);
    let dataLabels = gotChart.getElementsByClassName('highcharts-data-labels highcharts-series-' + seriesIndex);
    let ritScoreData = chart.series.find((t) => t.name.includes('RIT Score')).data;
    let labelIndex = 0;
    for (let ritScoreIndex = 0; ritScoreIndex < ritScoreData.length; ritScoreIndex++) {
        if (ritScoreData[ritScoreIndex].y != null) {
            dataLabels[0].childNodes[labelIndex].childNodes[0].setAttribute('rx', `${13 * sizeRatio}px`);
            dataLabels[0].childNodes[labelIndex].childNodes[0].setAttribute('ry', `${13 * sizeRatio}px`);
            dataLabels[0].childNodes[labelIndex].childNodes[0].setAttribute('width', `${45 * sizeRatio}px`);
            dataLabels[0].childNodes[labelIndex].childNodes[0].setAttribute('height', `${25 * sizeRatio}px`);
            dataLabels[0].childNodes[labelIndex].childNodes[0].setAttribute('x', `${-7 * sizeRatio}px`);
            if (addEventListener) {
                dataLabels[0].childNodes[labelIndex].addEventListener('click', function(event) {
                    const termDataSeries = chart.series.find((s) => s.name == 'Term Data');
                    const termColumn = termDataSeries.data.find((t) => t.x == ritScoreIndex);
                    termColumn.select();
                    event.stopPropagation();
                });
            }
            labelIndex++;
        }
    }
}

export function linkingStudyDataLabelClick(
    chart,
    collegeLinkingStudySeries,
    stateLinkingStudySeries,
    linkingStudyIndex,
    chartId
) {
    if (collegeLinkingStudySeries) {
        collegeLinkingStudySeries.forEach((study) => {
            addLabelClickEvent(study, linkingStudyIndex, chart, chartId);
            linkingStudyIndex = linkingStudyIndex + study.length;
        });
    }

    if (stateLinkingStudySeries) {
        addLabelClickEvent(stateLinkingStudySeries, linkingStudyIndex, chart, chartId);
    }
}

function findLinkingStudyTermIndex(linkingStudySeriesData) {
    let linkingStudyTermIndex = 0;

    for (let index = 0; index < linkingStudySeriesData.length; index++) {
        if (linkingStudySeriesData[index].y !== null) {
            linkingStudyTermIndex = index;
        }
    }
    return linkingStudyTermIndex;
}

function addLabelClickEvent(linkingStudySeries, linkingStudyIndex, chart, chartId) {
    let linkLabels = [];
    let gotChart = document.getElementById(chartId);
    let linkingStudyTermIndex = findLinkingStudyTermIndex(linkingStudySeries[0].data);

    for (let index = 0; index < linkingStudySeries.length; index++) {
        let seriesIndex = linkingStudyIndex + index;
        linkLabels = linkLabels.concat(
            gotChart.getElementsByClassName('highcharts-data-labels highcharts-series-' + seriesIndex)
        );
    }
    linkLabels.forEach(function(linkLabel) {
        if (linkLabel && linkLabel.length > 0) {
            linkLabel[0].addEventListener('click', function(event) {
                const termDataSeries = chart.series.find((s) => s.name == 'Term Data');
                const termColumn = termDataSeries.data[linkingStudyTermIndex];
                termColumn.select();
                event.stopPropagation();
            });
        }
    });
}

/**
 * Given a series start and end index this will grab the dataLabels from the chart object and test/adjust them to
 * make sure they do not overlap.
 * @param chart The chart object which should be updated
 * @param seriesStartIndex The start index of the series which holds the data for the column whose data labels we
 * are checking
 * @param seriesEndIndex The end index of the series which holds the data for the column whose data labels we are
 * checking
 * @param dataIndex The index at which the data is found within the series, i.e. for growth over time linking
 * studies are always found within the 12th index of the series the rest being null
 */
export function fixColumnChartDataLabels(chart, seriesStartIndex, seriesEndIndex, dataIndex) {
    if (seriesStartIndex && seriesEndIndex && dataIndex) {
        //get the series from the chart object so we can find its actual datalabel to figure out if it's overlapping
        const chartDataLabels = [];
        //Lets us find the top visible datalabel in addition to the top item which is not shown on the chart.
        let visibleTopFound = false;
        for (let i = seriesStartIndex; i <= seriesEndIndex; i++) {
            try {
                if (chart.series[i].points[dataIndex].dataLabel) {
                    //There is a label for this series so add it to our list so we can check for overlaps
                    var dataLabelInfo = {
                        moveMe: 0,
                        seriesIndex: i,
                        translateY: chart.series[i].points[dataIndex].dataLabel.translateY,
                        height: chart.series[i].points[dataIndex].dataLabel.height,
                    };
                    if (!visibleTopFound) {
                        dataLabelInfo.isTop = true;
                        if (dataLabelInfo.translateY >= 0) {
                            //0 is the top of the chart so this is visible
                            visibleTopFound = true;
                        }
                    } else if (i === seriesEndIndex) {
                        dataLabelInfo.isBottom = true;
                    }
                    chartDataLabels.push(dataLabelInfo);
                }
            } catch (e) {
                console.warn(
                    "Invalid data label index supplied: series index = '" +
                        seriesStartIndex +
                        "' points data index = '" +
                        dataIndex +
                        "'"
                );
            }
        }
        //This will determine which datalabels need to move then filter them out and actually move them by changing
        // the value of the translateY property of the datalabel of the chart.
        determineChartColumnDataLabelMove(chartDataLabels)
            .filter((dl) => {
                return dl.moveMe !== 0;
            })
            .forEach((item) => {
                var newY = (chart.series[item.seriesIndex].points[dataIndex].dataLabel.translateY += item.moveMe);
                chart.series[item.seriesIndex].points[dataIndex].dataLabel.attr({ translateY: newY });
            });
    }
}

/**
 * Determines how much a supplied list of chart dataLabels should be moved so that they do not intersect when displayed.
 * Note this just moves things up or down.
 *
 * @param dataLabels An object array holding information about the chart data labels. Expects dataLabelInfo objects in the form:
 * [{
 * moveMe: 0        The amount the label should be moved (should be set to 0 when calling this, meaning no move)
 * seriesIndex:     The index of the series within the chart the data label corresponds to,
 * translateY:      The current position of the label within the charts Y axis,
 * height:          The vertical height of the label within the chart,
 * },...]
 * @returns {*} A list of objects which say how much the labels must be adjusted by
 */
function determineChartColumnDataLabelMove(dataLabels) {
    if (!dataLabels) {
        return null;
    } else {
        //create a working copy of the supplied array and pass it into the adjustment method
        let chartDataLabels = getChartColumnDataLabelAdjustments(JSON.parse(JSON.stringify(dataLabels)));
        if (chartDataLabels) {
            //non-null result return the adjustment details
            return chartDataLabels;
        } else {
            //if null was returned it means that no adjustments work so just return the
            // initial array as supplied, we will leave the labels untouched
            return dataLabels;
        }
    }
}

/**
 * adjusts the supplied set of labels so they do not overlap
 * @param chartDataLabels an array of chart data labels
 * @returns {*} The adjusted array which shows how much the labels should be moved by, or null if an
 * unresolvable set was supplied
 */
function getChartColumnDataLabelAdjustments(chartDataLabels) {
    if (!chartDataLabels) {
        return null;
    }
    let adjusted = false;

    //Now compare each label from the top down, if a label is defined as being isTop we no longer want to move it so ignore those
    for (let index = 0; index < chartDataLabels.length; index++) {
        if (!chartDataLabels[index].isTop) {
            let overlapInfo = getLabelOverlapAdjustment(chartDataLabels[index - 1], chartDataLabels[index]);
            if (overlapInfo) {
                //We have overlapping labels
                if (chartDataLabels[index - 1].isTop && chartDataLabels[index].isBottom) {
                    //Unresolvable: bottom cannot move down, top cannot move up, and we have an overlap.
                    //In this case return null so that we can leave the labels untouched
                    if (!adjusted) {
                        console.info('Unable to resolve data label overlap.');
                        // Just return null in this case to signify an unresolvable set of labels
                        chartDataLabels = null;
                        break;
                    }
                } else if (chartDataLabels[index - 1].isTop) {
                    //It's at the top only the bottom label must move down
                    setLabelMoveAdjustment(chartDataLabels[index], overlapInfo.adjustHeight);
                    //Also since we moved it down from overlapping the top, it should become the new top
                    //as we don't want to move it back up again and recreate an overlap we already fixed.
                    chartDataLabels[index].isTop = true;
                    adjusted = true;
                } else if (chartDataLabels[index].isBottom) {
                    //Its at the bottom only the top label must move up
                    setLabelMoveAdjustment(chartDataLabels[index - 1], -Math.abs(overlapInfo.adjustHeight));
                    //Also since we moved it up from overlapping the bottom, it should become the new bottom
                    //as we don't want to move it back down again and recreate an overlap we already fixed.
                    chartDataLabels[index - 1].isBottom = true;
                    adjusted = true;
                } else {
                    //The labels are in the middle both labels should move half the overlap distance up/down
                    const heightDown = Math.floor(overlapInfo.adjustHeight / 2);
                    const heightUp = -Math.abs(Math.ceil(overlapInfo.adjustHeight / 2));

                    setLabelMoveAdjustment(chartDataLabels[index - 1], heightUp);
                    setLabelMoveAdjustment(chartDataLabels[index], heightDown);
                    adjusted = true;
                }
            }
        }
    }
    if (adjusted) {
        //Call again to make sure there are no overlaps caused by the last adjustment
        chartDataLabels = getChartColumnDataLabelAdjustments(chartDataLabels);
    }

    return chartDataLabels;
}

/**
 * Adjusts the data within the supplied datalabel to reflect the fact it has been moved
 * @param dataLabelInfo The data label info which is to be adjusted
 * @param adjustHeight the height which the label should be adjusted by (-ve means move up +ve move down).
 */
function setLabelMoveAdjustment(dataLabelInfo, adjustHeight) {
    dataLabelInfo.moveMe += adjustHeight;
    dataLabelInfo.translateY += adjustHeight;
}

/**
 * Determines if two data labels intersect by looking at the top height of the labels and the height of the labels
 * @param topLabel The top label
 * @param bottomLabel the bottom label
 * @returns {*} An object which says how much the labels must be adjusted by to no longer overlap
 */
function getLabelOverlapAdjustment(topLabel, bottomLabel) {
    //Add a gap so the labels no longer touch once adjusted
    const padding = 1;

    //0 means the very top of the graph
    let topLabelTop = topLabel.translateY,
        topLabelBottom = topLabel.translateY + topLabel.height,
        bottomLabelTop = bottomLabel.translateY,
        bottomLabelBottom = bottomLabel.translateY + bottomLabel.height;

    if (topLabelTop >= bottomLabelBottom || bottomLabelTop >= topLabelBottom) {
        //no intersection
        return null;
    }

    let maxTop = Math.max(topLabelTop, bottomLabelTop),
        minBottom = Math.min(topLabelBottom, bottomLabelBottom);

    return { adjustHeight: minBottom - maxTop + padding };
}

// This function assumes that an empty term is at the beginning and end of the series being supplied.
function enableDiscreteDataPoints(series) {
    if (!series) {
        return null;
    }

    series.forEach((s, index) => {
        // Ensure that there are points ahead and behind our index, and our index has a y value
        if (!s.y || !series[index - 1] || !series[index + 1]) {
            return;
        }

        // If both indexes above and below ours are null, add a marker to this data point
        if (series[index - 1].y === null && series[index + 1].y === null) {
            s.marker = { enabled: true };
        }
    });

    return series;
}

export default {
    extractDiscreteQuintileData,
    findTermsForChart,
    addGradeAndWeeksToTerms,
    findRitQuintilesForChart,
    populateRitQuintileSeries,
    populateCategoryNames,
    addPlotLine,
    populateGrowthGoalSeriesData,
    findMaxForSeries,
    findMinForSeries,
    findTermsForGOTChart,
    createDataSeries,
    populateRITScoreSeries,
    populateTypicalScoreSeries,
    addGradeAndWeeksToGOTTerms,
    findDistrictAveragesForChart,
    populateDistrictAveragesSeries,
    populateGoalSeries,
    populateXAxisMarkerSeries,
    populateProjectionSeries,
    findTestToProjectFrom,
    populateTermDataPopoverSeries,
    populateCurrentGoalData,
    populateLinkingStudySeries,
    findLinkingStudyByType,
    dataLabelFormatting,
    fixColumnChartDataLabels,
    linkingStudyDataLabelClick,
    enableDiscreteDataPoints,
    collegeLinkingStudyTypes,
};
